import { IJoinArgs, Layout } from "../../definitions";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useOutsideAlerter from "../hooks/ClickOutside";

interface IControls
{
    onMute: (type: "audio" | "video", enable: boolean) => void,
    onShareScreen?: (value: boolean) => void,
    onLeave?: () => void,
    onExpand?: () => void,
    onAddDevice?: () => void,
    onChangeLayout?: (layout: Layout) => void,
    initialValues?: IJoinArgs
    audioOnly?: boolean,
    screenShared?: boolean
    canShare: boolean,
    isExpanded?: boolean
}

const Controls = (props: IControls) => {
    const { t } = useTranslation(["videocall"]);

    const [trackState, setTrackState] = useState<IJoinArgs>({ 
        video: true, 
        audio: true
    });

    const [openedLayoutMenu, setOpenedLayoutMenu] = useState<boolean>(false)

    const selectionAreaRef = useRef(null)
    useOutsideAlerter(selectionAreaRef, () => setOpenedLayoutMenu(false))

    useEffect(() => {
        console.debug("Controls started. Inital values", props.initialValues);
        if(!props.initialValues) return;
        setTrackState(props.initialValues);
    }, [props.initialValues]);

    /**
     * Enable/disable main audio track
     */
    const muteAudio = (isleave?:boolean) => {
        const enable = isleave ? false : !trackState.audio;
        setTrackState((ps) => {
            return { ...ps, audio: !ps.audio };
        });
        props.onMute("audio", enable);
    }
    
    /**
     * Function to enable/disable main video track
     */
    const muteVideo = (isleave?:boolean) => {
        const enable = isleave ? false : !trackState.video;
        setTrackState((ps) => {
            return { ...ps, video: !ps.video };
        });
        props.onMute("video", enable);
    }

    //Leave and finish the videocall
    const onLeave = () => {
        if(props.onLeave){
            props.onLeave();
        }
    }
    /**
     * Function to show MediaDevicesModal
     */
    const onAddDevice = () => {
        if(props.onAddDevice){
            props.onAddDevice();
        }
    }

    /**
     * Function to start/stop share screen
     */
    const shareScreen = () => {
        if(props.onShareScreen)
            props.onShareScreen(!props.screenShared)
    }

    /**
     * Open layout menu
     * 
     * TODO: Change this implementation to detect clicks outside
     */
    const openLayoutMenu = () => {
        setOpenedLayoutMenu(!openedLayoutMenu)
    }

    /**
     * Validates and changes the selected layout
     * @param layout 
     */
    const changeLayout = (layout: Layout) => {
        setOpenedLayoutMenu(false)
        if(props.onChangeLayout)
            props.onChangeLayout(layout)
    }

    return (
        <div className="VideoCall__Actions">
            {/* Mute / Unmute video */}
            {/* Change to className="fas fa-video-slash" when deactivating video, return to className="fas fa-video" when activating it on click */}
            {
                !(props.audioOnly) &&
                <button 
                    title={t("buttons.camera.turn." + (trackState.video ? "off" : "on"))}
                    className={`fas ${trackState.video ? "fa-video" : "fa-video-slash"}`}
                    onClick={() => muteVideo()} />
            }

            {/* Mute / Unmute audio */}
            {/* Change to className="fas fa-microphone-slash" when deactivating video, return to className="fas fa-microphone" when activating it on click */}
            <button 
                title={t("buttons.microphone.turn." + (trackState.audio ? "off" : "on"))}
                className={`fas ${trackState.audio ? "fa-microphone" : "fa-microphone-slash"}`}
                onClick={() => muteAudio()} />

            {
                /* Change to className="fas fa-compress" when full screen video is active, return to className="fas fa-expand when minivideo is active */
                props && props.onExpand &&
                    <button 
                        title={t("buttons.screen.fullscrean." + (props.isExpanded ? "off" : "on"))}
                        className={`fas ${props.isExpanded ? "fa-compress" : "fa-expand"}`}
                        onClick={props.onExpand} />
            }
            
            {
                props && props.onAddDevice &&
                    <button 
                        title={t("buttons.devices.view")}
                        className="fas fa-computer" 
                        onClick={onAddDevice} />
            }

            {/* ShareScreen */}
            {
                props.canShare &&
                    <button
                        title={t(`buttons.screen.turn.${props.screenShared ? "off" : "on"}`)} 
                        className={`fas ${props.screenShared ? "fa-rectangle-xmark" : "fa-arrow-up-from-bracket"}`}
                        onClick={() => shareScreen()} />
            }

            {/* Choose view layout */}
            { props.onChangeLayout &&
                <div className="DropdownBlack" ref={selectionAreaRef}>
                    <button 
                        title={t("buttons.layout.view")}
                        className="fas fa-grip-horizontal" 
                        onClick={openLayoutMenu} />

                    {
                        openedLayoutMenu &&
                        <div className="DropdownBlack__Content">
                            <p className="DropdownBlack__Item"
                                onClick={() => changeLayout(Layout.SPOTLIGHT)}>
                                {t("buttons.layout.selection.spotlight")}
                            </p>
                            <p className="DropdownBlack__Item"
                                onClick={() => changeLayout(Layout.REGULAR)}>
                                {t("buttons.layout.selection.grid")}
                            </p>
                        </div>
                    }
                </div>
            }

            {/* Leave videocall */}
            {
                props && props.onLeave &&
                    <button 
                        title={t("buttons.call.leave")}
                        className="fas fa-phone-hangup" 
                        onClick={onLeave} />
            }
        </div>
    );
};

export default Controls;
